// ========== CHART CALCULATION HELPERS ==========
// All technical indicator algorithms extracted for maintainability

// ===== MOVING AVERAGES =====
export function calcSMA(data, period) {
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    const sum = data.slice(i - period + 1, i + 1).reduce((a, b) => a + b.close, 0);
    result.push({ time: data[i].time, value: sum / period });
  }
  return result;
}

export function calcEMA(data, period) {
  if (data.length < period) return [];
  const result = [];
  const k = 2 / (period + 1);
  let ema = data.slice(0, period).reduce((a, b) => a + b.close, 0) / period;
  result.push({ time: data[period - 1].time, value: ema });
  for (let i = period; i < data.length; i++) {
    ema = data[i].close * k + ema * (1 - k);
    result.push({ time: data[i].time, value: ema });
  }
  return result;
}

export function calcEMAFromValues(values, period) {
  if (values.length < period) return [];
  const result = [];
  const k = 2 / (period + 1);
  let ema = values.slice(0, period).reduce((a, b) => a + b, 0) / period;
  result.push(ema);
  for (let i = period; i < values.length; i++) {
    ema = values[i] * k + ema * (1 - k);
    result.push(ema);
  }
  return result;
}

// ===== BOLLINGER BANDS =====
export function calcBollingerBands(data, period = 20, multiplier = 2) {
  const upper = [], lower = [], middle = [];
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1).map(d => d.close);
    const sma = slice.reduce((a, b) => a + b, 0) / period;
    const std = Math.sqrt(slice.reduce((sum, c) => sum + Math.pow(c - sma, 2), 0) / period);
    upper.push({ time: data[i].time, value: sma + std * multiplier });
    middle.push({ time: data[i].time, value: sma });
    lower.push({ time: data[i].time, value: sma - std * multiplier });
  }
  return { upper, middle, lower };
}

// ===== RSI =====
export function calcRSI(data, period = 14) {
  if (data.length < period + 1) return [];
  const result = [];
  let gains = 0, losses = 0;
  for (let i = 1; i <= period; i++) {
    const change = data[i].close - data[i - 1].close;
    if (change > 0) gains += change; else losses -= change;
  }
  let avgGain = gains / period, avgLoss = losses / period;
  let rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
  result.push({ time: data[period].time, value: 100 - 100 / (1 + rs) });
  for (let i = period + 1; i < data.length; i++) {
    const change = data[i].close - data[i - 1].close;
    avgGain = (avgGain * (period - 1) + (change > 0 ? change : 0)) / period;
    avgLoss = (avgLoss * (period - 1) + (change < 0 ? -change : 0)) / period;
    rs = avgLoss === 0 ? 100 : avgGain / avgLoss;
    result.push({ time: data[i].time, value: 100 - 100 / (1 + rs) });
  }
  return result;
}

// ===== MACD =====
export function calcMACD(data, fast = 12, slow = 26, signal = 9) {
  if (data.length < slow + signal) return { macd: [], signal: [], histogram: [] };
  const closes = data.map(d => d.close);
  const emaFast = calcEMAFromValues(closes, fast);
  const emaSlow = calcEMAFromValues(closes, slow);
  const offset = slow - fast;
  const macdLine = [];
  for (let i = 0; i < emaSlow.length; i++) {
    macdLine.push(emaFast[i + offset] - emaSlow[i]);
  }
  const signalLine = calcEMAFromValues(macdLine, signal);
  const macd = [], sig = [], hist = [];
  const macdOffset = signal - 1;
  for (let i = 0; i < signalLine.length; i++) {
    const idx = slow - 1 + macdOffset + i;
    if (idx < data.length) {
      macd.push({ time: data[idx].time, value: macdLine[i + macdOffset] });
      sig.push({ time: data[idx].time, value: signalLine[i] });
      const h = macdLine[i + macdOffset] - signalLine[i];
      hist.push({ time: data[idx].time, value: h, color: h >= 0 ? '#26a69a' : '#ef5350' });
    }
  }
  return { macd, signal: sig, histogram: hist };
}

// ===== ATR =====
export function calcATR(data, period = 14) {
  if (data.length < period + 1) return [];
  const result = [];
  const trueRanges = [];
  for (let i = 1; i < data.length; i++) {
    const tr = Math.max(
      data[i].high - data[i].low,
      Math.abs(data[i].high - data[i - 1].close),
      Math.abs(data[i].low - data[i - 1].close)
    );
    trueRanges.push(tr);
  }
  let atr = trueRanges.slice(0, period).reduce((a, b) => a + b, 0) / period;
  result.push({ time: data[period].time, value: atr });
  for (let i = period; i < trueRanges.length; i++) {
    atr = (atr * (period - 1) + trueRanges[i]) / period;
    result.push({ time: data[i + 1].time, value: atr });
  }
  return result;
}

// ===== ADX =====
export function calcADX(data, period = 14) {
  if (data.length < period * 2 + 1) return [];
  const result = [];
  const plusDM = [], minusDM = [], tr = [];
  for (let i = 1; i < data.length; i++) {
    const upMove = data[i].high - data[i - 1].high;
    const downMove = data[i - 1].low - data[i].low;
    plusDM.push(upMove > downMove && upMove > 0 ? upMove : 0);
    minusDM.push(downMove > upMove && downMove > 0 ? downMove : 0);
    tr.push(Math.max(data[i].high - data[i].low, Math.abs(data[i].high - data[i - 1].close), Math.abs(data[i].low - data[i - 1].close)));
  }
  let smoothTR = tr.slice(0, period).reduce((a, b) => a + b, 0);
  let smoothPDM = plusDM.slice(0, period).reduce((a, b) => a + b, 0);
  let smoothMDM = minusDM.slice(0, period).reduce((a, b) => a + b, 0);
  const dx = [];
  for (let i = period; i < tr.length; i++) {
    smoothTR = smoothTR - smoothTR / period + tr[i];
    smoothPDM = smoothPDM - smoothPDM / period + plusDM[i];
    smoothMDM = smoothMDM - smoothMDM / period + minusDM[i];
    const pdi = smoothTR > 0 ? (smoothPDM / smoothTR) * 100 : 0;
    const mdi = smoothTR > 0 ? (smoothMDM / smoothTR) * 100 : 0;
    const dxVal = pdi + mdi > 0 ? Math.abs(pdi - mdi) / (pdi + mdi) * 100 : 0;
    dx.push(dxVal);
  }
  if (dx.length < period) return [];
  let adx = dx.slice(0, period).reduce((a, b) => a + b, 0) / period;
  const startIdx = period * 2;
  if (startIdx < data.length) result.push({ time: data[startIdx].time, value: adx });
  for (let i = period; i < dx.length; i++) {
    adx = (adx * (period - 1) + dx[i]) / period;
    const idx = i + period + 1;
    if (idx < data.length) result.push({ time: data[idx].time, value: adx });
  }
  return result;
}

// ===== HEIKIN ASHI =====
export function calcHeikinAshi(data) {
  if (!data.length) return [];
  const result = [];
  let prevHA = { open: data[0].open, close: (data[0].open + data[0].high + data[0].low + data[0].close) / 4 };
  result.push({
    time: data[0].time,
    open: prevHA.open,
    high: Math.max(data[0].high, prevHA.open, prevHA.close),
    low: Math.min(data[0].low, prevHA.open, prevHA.close),
    close: prevHA.close,
  });
  for (let i = 1; i < data.length; i++) {
    const haClose = (data[i].open + data[i].high + data[i].low + data[i].close) / 4;
    const haOpen = (prevHA.open + prevHA.close) / 2;
    result.push({
      time: data[i].time,
      open: haOpen,
      high: Math.max(data[i].high, haOpen, haClose),
      low: Math.min(data[i].low, haOpen, haClose),
      close: haClose,
    });
    prevHA = { open: haOpen, close: haClose };
  }
  return result;
}

// ===== STOCHASTIC =====
export function calcStochastic(data, kPeriod = 14, dPeriod = 3) {
  if (data.length < kPeriod) return { k: [], d: [] };
  const kValues = [];
  for (let i = kPeriod - 1; i < data.length; i++) {
    const slice = data.slice(i - kPeriod + 1, i + 1);
    const high = Math.max(...slice.map(d => d.high));
    const low = Math.min(...slice.map(d => d.low));
    const k = high === low ? 50 : ((data[i].close - low) / (high - low)) * 100;
    kValues.push({ time: data[i].time, value: k });
  }
  const dValues = [];
  for (let i = dPeriod - 1; i < kValues.length; i++) {
    const avg = kValues.slice(i - dPeriod + 1, i + 1).reduce((a, b) => a + b.value, 0) / dPeriod;
    dValues.push({ time: kValues[i].time, value: avg });
  }
  return { k: kValues, d: dValues };
}

// ===== PARABOLIC SAR =====
export function calcParabolicSAR(data, accelStart = 0.02, accelMax = 0.2) {
  if (data.length < 2) return [];
  const result = [];
  let isLong = data[1].close > data[0].close;
  let sar = isLong ? data[0].low : data[0].high;
  let ep = isLong ? data[0].high : data[0].low;
  let af = accelStart;
  result.push({ time: data[0].time, value: sar });
  for (let i = 1; i < data.length; i++) {
    sar = sar + af * (ep - sar);
    if (isLong) {
      if (i > 1) sar = Math.min(sar, data[i - 1].low, data[i - 2]?.low ?? data[i - 1].low);
      if (data[i].low < sar) {
        isLong = false; sar = ep; ep = data[i].low; af = accelStart;
      } else {
        if (data[i].high > ep) { ep = data[i].high; af = Math.min(af + accelStart, accelMax); }
      }
    } else {
      if (i > 1) sar = Math.max(sar, data[i - 1].high, data[i - 2]?.high ?? data[i - 1].high);
      if (data[i].high > sar) {
        isLong = true; sar = ep; ep = data[i].high; af = accelStart;
      } else {
        if (data[i].low < ep) { ep = data[i].low; af = Math.min(af + accelStart, accelMax); }
      }
    }
    result.push({ time: data[i].time, value: sar, color: isLong ? '#26a69a' : '#ef5350' });
  }
  return result;
}

// ===== SUPERTREND =====
export function calcSuperTrend(data, period = 10, multiplier = 3) {
  const atr = calcATR(data, period);
  if (!atr.length) return [];
  const result = [];
  const atrMap = {};
  atr.forEach(a => { atrMap[a.time] = a.value; });
  let upperBand = 0, lowerBand = 0, supertrend = 0, prevSupertrend = 0, prevUpperBand = 0, prevLowerBand = 0;
  let isUpTrend = true;
  for (let i = period; i < data.length; i++) {
    const atrVal = atrMap[data[i].time];
    if (!atrVal) continue;
    const hl2 = (data[i].high + data[i].low) / 2;
    upperBand = hl2 + multiplier * atrVal;
    lowerBand = hl2 - multiplier * atrVal;
    if (prevLowerBand && lowerBand < prevLowerBand && data[i - 1].close > prevLowerBand) lowerBand = prevLowerBand;
    if (prevUpperBand && upperBand > prevUpperBand && data[i - 1].close < prevUpperBand) upperBand = prevUpperBand;
    if (prevSupertrend === prevUpperBand) {
      isUpTrend = data[i].close > upperBand;
    } else {
      isUpTrend = data[i].close > lowerBand;
    }
    supertrend = isUpTrend ? lowerBand : upperBand;
    result.push({ time: data[i].time, value: supertrend, color: isUpTrend ? '#26a69a' : '#ef5350' });
    prevSupertrend = supertrend; prevUpperBand = upperBand; prevLowerBand = lowerBand;
  }
  return result;
}

// ===== KELTNER CHANNELS =====
export function calcKeltnerChannels(data, emaPeriod = 20, atrPeriod = 10, multiplier = 2) {
  const emaData = calcEMA(data, emaPeriod);
  const atrData = calcATR(data, atrPeriod);
  if (!emaData.length || !atrData.length) return { upper: [], middle: [], lower: [] };
  const atrMap = {};
  atrData.forEach(a => { atrMap[a.time] = a.value; });
  const upper = [], middle = [], lower = [];
  emaData.forEach(e => {
    const atr = atrMap[e.time];
    if (atr) {
      upper.push({ time: e.time, value: e.value + multiplier * atr });
      middle.push({ time: e.time, value: e.value });
      lower.push({ time: e.time, value: e.value - multiplier * atr });
    }
  });
  return { upper, middle, lower };
}

// ===== DONCHIAN CHANNELS =====
export function calcDonchianChannels(data, period = 20) {
  const upper = [], lower = [], middle = [];
  for (let i = period - 1; i < data.length; i++) {
    const slice = data.slice(i - period + 1, i + 1);
    const high = Math.max(...slice.map(d => d.high));
    const low = Math.min(...slice.map(d => d.low));
    upper.push({ time: data[i].time, value: high });
    lower.push({ time: data[i].time, value: low });
    middle.push({ time: data[i].time, value: (high + low) / 2 });
  }
  return { upper, middle, lower };
}

// ===== STD DEV BANDS =====
export function calcStdDevBands(data, period = 20, multiplier = 2) {
  return calcBollingerBands(data, period, multiplier); // Same algorithm
}

// ===== HISTORICAL VOLATILITY =====
export function calcHistoricalVolatility(data, period = 20) {
  if (data.length < period + 1) return [];
  const result = [];
  const logReturns = [];
  for (let i = 1; i < data.length; i++) {
    logReturns.push(Math.log(data[i].close / data[i - 1].close));
  }
  for (let i = period - 1; i < logReturns.length; i++) {
    const slice = logReturns.slice(i - period + 1, i + 1);
    const mean = slice.reduce((a, b) => a + b, 0) / period;
    const variance = slice.reduce((sum, r) => sum + Math.pow(r - mean, 2), 0) / (period - 1);
    const hv = Math.sqrt(variance * 252) * 100; // Annualized
    result.push({ time: data[i + 1].time, value: hv });
  }
  return result;
}

// ===== VWAP =====
export function calcVWAP(data) {
  if (!data.length) return [];
  const result = [];
  let cumVolPrice = 0, cumVol = 0;
  for (let i = 0; i < data.length; i++) {
    const tp = (data[i].high + data[i].low + data[i].close) / 3;
    const vol = data[i].volume || 1;
    cumVolPrice += tp * vol;
    cumVol += vol;
    result.push({ time: data[i].time, value: cumVol > 0 ? cumVolPrice / cumVol : tp });
  }
  return result;
}

// ===== VWAP BANDS =====
export function calcVWAPBands(data, stdMultiplier = 2) {
  if (!data.length) return { vwap: [], upper: [], lower: [] };
  const vwap = [], upper = [], lower = [];
  let cumVolPrice = 0, cumVol = 0, cumVolPriceSq = 0;
  for (let i = 0; i < data.length; i++) {
    const tp = (data[i].high + data[i].low + data[i].close) / 3;
    const vol = data[i].volume || 1;
    cumVolPrice += tp * vol;
    cumVol += vol;
    cumVolPriceSq += tp * tp * vol;
    const vwapVal = cumVol > 0 ? cumVolPrice / cumVol : tp;
    const variance = cumVol > 0 ? (cumVolPriceSq / cumVol) - vwapVal * vwapVal : 0;
    const std = Math.sqrt(Math.max(0, variance));
    vwap.push({ time: data[i].time, value: vwapVal });
    upper.push({ time: data[i].time, value: vwapVal + std * stdMultiplier });
    lower.push({ time: data[i].time, value: vwapVal - std * stdMultiplier });
  }
  return { vwap, upper, lower };
}

// ===== OBV =====
export function calcOBV(data) {
  if (data.length < 2) return [];
  const result = [{ time: data[0].time, value: 0 }];
  let obv = 0;
  for (let i = 1; i < data.length; i++) {
    const vol = data[i].volume || 0;
    if (data[i].close > data[i - 1].close) obv += vol;
    else if (data[i].close < data[i - 1].close) obv -= vol;
    result.push({ time: data[i].time, value: obv });
  }
  return result;
}

// ===== ACCUMULATION/DISTRIBUTION LINE =====
export function calcADLine(data) {
  if (!data.length) return [];
  const result = [];
  let ad = 0;
  for (let i = 0; i < data.length; i++) {
    const hlRange = data[i].high - data[i].low;
    const mfm = hlRange > 0 ? ((data[i].close - data[i].low) - (data[i].high - data[i].close)) / hlRange : 0;
    ad += mfm * (data[i].volume || 1);
    result.push({ time: data[i].time, value: ad });
  }
  return result;
}

// ===== CHAIKIN MONEY FLOW =====
export function calcCMF(data, period = 20) {
  if (data.length < period) return [];
  const result = [];
  for (let i = period - 1; i < data.length; i++) {
    let mfvSum = 0, volSum = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const hlRange = data[j].high - data[j].low;
      const mfm = hlRange > 0 ? ((data[j].close - data[j].low) - (data[j].high - data[j].close)) / hlRange : 0;
      const vol = data[j].volume || 1;
      mfvSum += mfm * vol;
      volSum += vol;
    }
    result.push({ time: data[i].time, value: volSum > 0 ? mfvSum / volSum : 0 });
  }
  return result;
}

// ===== MFI =====
export function calcMFI(data, period = 14) {
  if (data.length < period + 1) return [];
  const result = [];
  for (let i = period; i < data.length; i++) {
    let posFlow = 0, negFlow = 0;
    for (let j = i - period + 1; j <= i; j++) {
      const tp = (data[j].high + data[j].low + data[j].close) / 3;
      const prevTp = (data[j - 1].high + data[j - 1].low + data[j - 1].close) / 3;
      const rawMF = tp * (data[j].volume || 1);
      if (tp > prevTp) posFlow += rawMF; else negFlow += rawMF;
    }
    const mfi = negFlow > 0 ? 100 - 100 / (1 + posFlow / negFlow) : 100;
    result.push({ time: data[i].time, value: mfi });
  }
  return result;
}

// ===== DELTA (BUY-SELL VOLUME) =====
export function calcDelta(data) {
  if (!data.length) return [];
  return data.map(d => {
    const vol = d.volume || 0;
    const range = d.high - d.low;
    const buyRatio = range > 0 ? (d.close - d.low) / range : 0.5;
    const delta = vol * (buyRatio - (1 - buyRatio));
    return { time: d.time, value: delta, color: delta >= 0 ? '#26a69a' : '#ef5350' };
  });
}

// ===== CUMULATIVE DELTA =====
export function calcCumulativeDelta(data) {
  const delta = calcDelta(data);
  let cum = 0;
  return delta.map(d => { cum += d.value; return { time: d.time, value: cum }; });
}

// ===== VOLUME PROFILE =====
export function calcVolumeProfile(data, levels = 24) {
  if (!data.length) return [];
  const allHigh = Math.max(...data.map(d => d.high));
  const allLow = Math.min(...data.map(d => d.low));
  const step = (allHigh - allLow) / levels;
  if (step <= 0) return [];
  const profile = Array.from({ length: levels }, (_, i) => ({
    price: allLow + step * (i + 0.5),
    volume: 0,
  }));
  data.forEach(d => {
    const vol = d.volume || 1;
    const idx = Math.min(Math.floor((d.close - allLow) / step), levels - 1);
    if (idx >= 0 && idx < levels) profile[idx].volume += vol;
  });
  return profile;
}

// ===== RENKO =====
export function calcRenko(data, boxSize = null) {
  if (data.length < 2) return [];
  if (!boxSize) {
    const range = Math.max(...data.map(d => d.high)) - Math.min(...data.map(d => d.low));
    boxSize = +(range / 20).toFixed(4);
  }
  if (boxSize <= 0) return [];

  // Renko bricks are time-independent — assign evenly-spaced sequential
  // timestamps so every brick occupies the same width with no gaps.
  const toSec = (t) =>
    typeof t === 'number' ? t : Math.floor(new Date(t + 'T00:00:00Z').getTime() / 1000);
  const firstTime = toSec(data[0].time);
  const lastSrcTime = toSec(data[data.length - 1].time);
  // Average candle duration keeps the date scale meaningful
  const avgInterval = Math.max(60, Math.floor((lastSrcTime - firstTime) / Math.max(data.length - 1, 1)));

  const result = [];
  let lastClose = Math.round(data[0].close / boxSize) * boxSize;
  let brickNum = 0;

  for (let i = 1; i < data.length; i++) {
    const diff = data[i].close - lastClose;
    // Small epsilon prevents floating-point shortfall (e.g. 1.9999… → 2 bricks)
    const bricks = Math.floor(Math.abs(diff) / boxSize + 1e-10);
    for (let j = 0; j < bricks; j++) {
      const open = +lastClose.toFixed(4);
      lastClose = diff > 0 ? lastClose + boxSize : lastClose - boxSize;
      const close = +lastClose.toFixed(4);
      brickNum++;
      result.push({
        // Sequential timestamp: each brick is exactly avgInterval apart → no gaps
        time: firstTime + brickNum * avgInterval,
        open, close,
        high: Math.max(open, close),
        low: Math.min(open, close),
      });
    }
  }
  return result;
}

// ===== KAGI =====
export function calcKagi(data, reversalAmount = null) {
  if (data.length < 2) return [];
  if (!reversalAmount) {
    const range = Math.max(...data.map(d => d.high)) - Math.min(...data.map(d => d.low));
    reversalAmount = range * 0.04;
  }
  const result = [];
  let direction = data[1].close >= data[0].close ? 1 : -1;
  let lastPrice = data[0].close;
  let currentHigh = data[0].close, currentLow = data[0].close;
  for (let i = 1; i < data.length; i++) {
    const price = data[i].close;
    if (direction === 1) {
      if (price > currentHigh) currentHigh = price;
      if (currentHigh - price >= reversalAmount) {
        result.push({ time: data[i].time, open: lastPrice, close: currentHigh, high: currentHigh, low: Math.min(lastPrice, currentHigh) });
        lastPrice = currentHigh; currentLow = price; direction = -1;
      }
    } else {
      if (price < currentLow) currentLow = price;
      if (price - currentLow >= reversalAmount) {
        result.push({ time: data[i].time, open: lastPrice, close: currentLow, high: Math.max(lastPrice, currentLow), low: currentLow });
        lastPrice = currentLow; currentHigh = price; direction = 1;
      }
    }
  }
  if (result.length === 0 && data.length > 0) {
    result.push({ time: data[data.length - 1].time, open: data[0].close, close: data[data.length - 1].close, high: Math.max(data[0].close, data[data.length - 1].close), low: Math.min(data[0].close, data[data.length - 1].close) });
  }
  return result;
}

// ===== LINE BREAK =====
export function calcLineBreak(data, lineCount = 3) {
  if (data.length < 2) return [];
  const result = [];
  result.push({ time: data[0].time, open: data[0].open, close: data[0].close, high: Math.max(data[0].open, data[0].close), low: Math.min(data[0].open, data[0].close) });
  for (let i = 1; i < data.length; i++) {
    const lastLine = result[result.length - 1];
    const isUp = lastLine.close >= lastLine.open;
    if (isUp && data[i].close > lastLine.close) {
      result.push({ time: data[i].time, open: lastLine.close, close: data[i].close, high: data[i].close, low: lastLine.close });
    } else if (!isUp && data[i].close < lastLine.close) {
      result.push({ time: data[i].time, open: lastLine.close, close: data[i].close, high: lastLine.close, low: data[i].close });
    } else {
      const lookback = Math.min(lineCount, result.length);
      const recentLines = result.slice(-lookback);
      const minClose = Math.min(...recentLines.map(l => Math.min(l.open, l.close)));
      const maxClose = Math.max(...recentLines.map(l => Math.max(l.open, l.close)));
      if (data[i].close > maxClose) {
        result.push({ time: data[i].time, open: lastLine.close, close: data[i].close, high: data[i].close, low: lastLine.close });
      } else if (data[i].close < minClose) {
        result.push({ time: data[i].time, open: lastLine.close, close: data[i].close, high: lastLine.close, low: data[i].close });
      }
    }
  }
  return result;
}

// ===== RANGE BARS =====
export function calcRangeBars(data, rangeSize = null) {
  if (data.length < 2) return [];
  if (!rangeSize) {
    const atrValues = [];
    for (let i = 1; i < Math.min(15, data.length); i++) {
      atrValues.push(data[i].high - data[i].low);
    }
    rangeSize = atrValues.reduce((a, b) => a + b, 0) / atrValues.length;
  }
  const result = [];
  let currentOpen = data[0].open, currentHigh = data[0].high, currentLow = data[0].low;
  for (let i = 0; i < data.length; i++) {
    currentHigh = Math.max(currentHigh, data[i].high);
    currentLow = Math.min(currentLow, data[i].low);
    if (currentHigh - currentLow >= rangeSize) {
      result.push({ time: data[i].time, open: currentOpen, close: data[i].close, high: currentHigh, low: currentLow });
      currentOpen = data[i].close; currentHigh = data[i].close; currentLow = data[i].close;
    }
  }
  return result;
}

// ===== POINT & FIGURE =====
export function calcPointFigure(data, boxSize = null, reversalBoxes = 3) {
  if (data.length < 2) return [];
  if (!boxSize) {
    const range = Math.max(...data.map(d => d.high)) - Math.min(...data.map(d => d.low));
    boxSize = +(range / 20).toFixed(4);
  }
  if (boxSize <= 0) return [];

  const toSec = (t) => typeof t === 'number' ? t : Math.floor(new Date(t + 'T00:00:00Z').getTime() / 1000);
  const firstTime = toSec(data[0].time);
  const lastSrcTime = toSec(data[data.length - 1].time);
  const avgInterval = Math.max(60, Math.floor((lastSrcTime - firstTime) / Math.max(data.length - 1, 1)));

  const result = [];
  let brickNum = 0;
  let direction = 1; // 1 = X (up column), -1 = O (down column)
  let colHigh = Math.ceil(data[0].close / boxSize) * boxSize;
  let colLow = Math.floor(data[0].close / boxSize) * boxSize;

  const pushBrick = (open, close) => {
    brickNum++;
    result.push({
      time: firstTime + brickNum * avgInterval,
      open: +open.toFixed(4),
      close: +close.toFixed(4),
      high: +Math.max(open, close).toFixed(4),
      low: +Math.min(open, close).toFixed(4),
    });
  };

  for (let i = 1; i < data.length; i++) {
    const h = data[i].high;
    const l = data[i].low;

    if (direction === 1) {
      // Continuing up (X column)
      if (h >= colHigh + boxSize) {
        const boxes = Math.floor((h - colHigh) / boxSize + 1e-10);
        for (let j = 0; j < boxes; j++) {
          pushBrick(colHigh, colHigh + boxSize);
          colHigh += boxSize;
        }
      }
      // Reversal down?
      else if (l <= colHigh - reversalBoxes * boxSize) {
        direction = -1;
        const newLow = Math.floor(l / boxSize) * boxSize;
        const boxes = Math.max(reversalBoxes, Math.round((colHigh - newLow) / boxSize));
        for (let j = 0; j < boxes; j++) {
          pushBrick(colHigh - j * boxSize, colHigh - (j + 1) * boxSize);
        }
        colLow = colHigh - boxes * boxSize;
        colHigh = colHigh;
      }
    } else {
      // Continuing down (O column)
      if (l <= colLow - boxSize) {
        const boxes = Math.floor((colLow - l) / boxSize + 1e-10);
        for (let j = 0; j < boxes; j++) {
          pushBrick(colLow, colLow - boxSize);
          colLow -= boxSize;
        }
      }
      // Reversal up?
      else if (h >= colLow + reversalBoxes * boxSize) {
        direction = 1;
        const newHigh = Math.ceil(h / boxSize) * boxSize;
        const boxes = Math.max(reversalBoxes, Math.round((newHigh - colLow) / boxSize));
        for (let j = 0; j < boxes; j++) {
          pushBrick(colLow + j * boxSize, colLow + (j + 1) * boxSize);
        }
        colHigh = colLow + boxes * boxSize;
        colLow = colLow;
      }
    }
  }

  if (!result.length && data.length > 1) {
    pushBrick(data[0].close, data[data.length - 1].close);
  }
  return result;
}

// ===== PATTERN DETECTION =====
export function findPivots(data, leftBars = 5, rightBars = 5) {
  const highs = [], lows = [];
  for (let i = leftBars; i < data.length - rightBars; i++) {
    let isHigh = true, isLow = true;
    for (let j = 1; j <= leftBars; j++) {
      if (data[i].high <= data[i - j].high) isHigh = false;
      if (data[i].low >= data[i - j].low) isLow = false;
    }
    for (let j = 1; j <= rightBars; j++) {
      if (data[i].high <= data[i + j].high) isHigh = false;
      if (data[i].low >= data[i + j].low) isLow = false;
    }
    if (isHigh) highs.push({ index: i, price: data[i].high, time: data[i].time });
    if (isLow) lows.push({ index: i, price: data[i].low, time: data[i].time });
  }
  return { highs, lows };
}

export function detectAllPatterns(data) {
  if (data.length < 30) return [];
  const { highs, lows } = findPivots(data);
  const patterns = [];
  // Double Top
  for (let i = 0; i < highs.length - 1; i++) {
    const diff = Math.abs(highs[i].price - highs[i + 1].price) / highs[i].price;
    if (diff < 0.02 && highs[i + 1].index - highs[i].index > 5) {
      patterns.push({ name: 'Double Top', type: 'bearish', confidence: Math.round((1 - diff / 0.02) * 80 + 20), start: highs[i].time, end: highs[i + 1].time });
    }
  }
  // Double Bottom
  for (let i = 0; i < lows.length - 1; i++) {
    const diff = Math.abs(lows[i].price - lows[i + 1].price) / lows[i].price;
    if (diff < 0.02 && lows[i + 1].index - lows[i].index > 5) {
      patterns.push({ name: 'Double Bottom', type: 'bullish', confidence: Math.round((1 - diff / 0.02) * 80 + 20), start: lows[i].time, end: lows[i + 1].time });
    }
  }
  // Head & Shoulders
  if (highs.length >= 3) {
    for (let i = 0; i < highs.length - 2; i++) {
      const [l, h, r] = [highs[i], highs[i + 1], highs[i + 2]];
      if (h.price > l.price && h.price > r.price && Math.abs(l.price - r.price) / l.price < 0.03) {
        patterns.push({ name: 'Head & Shoulders', type: 'bearish', confidence: 70, start: l.time, end: r.time });
      }
    }
  }
  // Inverse H&S
  if (lows.length >= 3) {
    for (let i = 0; i < lows.length - 2; i++) {
      const [l, h, r] = [lows[i], lows[i + 1], lows[i + 2]];
      if (h.price < l.price && h.price < r.price && Math.abs(l.price - r.price) / l.price < 0.03) {
        patterns.push({ name: 'Inv Head & Shoulders', type: 'bullish', confidence: 70, start: l.time, end: r.time });
      }
    }
  }
  // Triangles
  if (highs.length >= 2 && lows.length >= 2) {
    const lastHighs = highs.slice(-2);
    const lastLows = lows.slice(-2);
    const highSlope = (lastHighs[1].price - lastHighs[0].price) / (lastHighs[1].index - lastHighs[0].index);
    const lowSlope = (lastLows[1].price - lastLows[0].price) / (lastLows[1].index - lastLows[0].index);
    if (highSlope < -0.001 && lowSlope > 0.001) patterns.push({ name: 'Symmetrical Triangle', type: 'neutral', confidence: 60 });
    else if (Math.abs(highSlope) < 0.001 && lowSlope > 0.001) patterns.push({ name: 'Ascending Triangle', type: 'bullish', confidence: 65 });
    else if (highSlope < -0.001 && Math.abs(lowSlope) < 0.001) patterns.push({ name: 'Descending Triangle', type: 'bearish', confidence: 65 });
    if (highSlope > 0.001 && lowSlope > 0.001 && highSlope < lowSlope) patterns.push({ name: 'Rising Wedge', type: 'bearish', confidence: 55 });
    if (highSlope < -0.001 && lowSlope < -0.001 && highSlope > lowSlope) patterns.push({ name: 'Falling Wedge', type: 'bullish', confidence: 55 });
  }
  // Channels & Flags
  if (highs.length >= 2 && lows.length >= 2) {
    const lastHighs = highs.slice(-2);
    const lastLows = lows.slice(-2);
    const highSlope = (lastHighs[1].price - lastHighs[0].price) / (lastHighs[1].index - lastHighs[0].index);
    const lowSlope = (lastLows[1].price - lastLows[0].price) / (lastLows[1].index - lastLows[0].index);
    if (highSlope > 0.001 && lowSlope > 0.001 && Math.abs(highSlope - lowSlope) / Math.abs(highSlope) < 0.3)
      patterns.push({ name: 'Ascending Channel', type: 'bullish', confidence: 60 });
    if (highSlope < -0.001 && lowSlope < -0.001 && Math.abs(highSlope - lowSlope) / Math.abs(highSlope) < 0.3)
      patterns.push({ name: 'Descending Channel', type: 'bearish', confidence: 60 });
    const span = (lastHighs[1].index - lastHighs[0].index);
    if (span < 15 && span > 3) {
      if (highSlope < -0.001 && lowSlope < -0.001) patterns.push({ name: 'Bull Flag', type: 'bullish', confidence: 55 });
      if (highSlope > 0.001 && lowSlope > 0.001) patterns.push({ name: 'Bear Flag', type: 'bearish', confidence: 55 });
    }
  }
  return patterns;
}
